import { createListenerMiddleware } from '@reduxjs/toolkit';
import { monitoring } from 'common/services';
import { selectPathname } from 'modules/router/state/router.selectors';
import { NATALIE_EVENT_ID_CONTEXT_KEY, resolveNatalieEventId } from 'pages/Natalie/telemetry/natalieTelemetry';
import { LOCATION_CHANGE } from 'redux-first-history';

import type { TypedStartListening } from '@reduxjs/toolkit';
import type { CombinedDispatch, RootState } from 'store';

type AppStartListening = TypedStartListening<RootState, CombinedDispatch>;

/**
 * Tags the Datadog RUM + Logs global context with `natalie_event_id` while a participant is on a
 * Natalie route whose URL carries the event id, so all browser telemetry (views, actions, resources,
 * errors, session replay, forwarded logs) is correlatable with the backend `natalie_event_id`.
 *
 * Driven off `LOCATION_CHANGE` (redux-first-history) rather than a mounted component: navigation is a
 * Redux side effect, the pathname is authoritative in `state.router`, and a single store-level
 * listener covers both the PreApp (pre-auth/accountless) and Dashboard (authed) route trees — no
 * mount-point gymnastics. Mirrors the `nattering.telemetry.listener.ts` pattern.
 */
export const natalieTelemetryListener = createListenerMiddleware();
const startListening = natalieTelemetryListener.startListening as AppStartListening;

// The id currently set on the global context. Module-scoped so we only touch the SDK when the
// resolved id actually changes — no churn when navigating between sub-pages of the same event.
let currentEventId: string | undefined;

startListening({
  type: LOCATION_CHANGE,
  effect: (_action, { getState }) => {
    const eventId = resolveNatalieEventId(selectPathname(getState()));
    if (eventId === currentEventId) return;

    currentEventId = eventId;
    if (eventId) {
      monitoring.setContext(NATALIE_EVENT_ID_CONTEXT_KEY, eventId);
    } else {
      monitoring.removeContext(NATALIE_EVENT_ID_CONTEXT_KEY);
    }
  },
});
